import React, { useState, useEffect, useRef } from "react";
import "./UniversalDetailsPopup.css";
import { Button, TextField, Select, MenuItem } from "@mui/material";
import { toast } from "react-toastify";

const UniversalDetailsPopup = ({
  open,
  row,
  onClose,
  onSave,
  onDelete,
  fieldLabels = {},
  hiddenFields = [],
  nonEditableFields = [],
  fieldTypes = {},
  allowedColumns = [],
}) => {
  const [formData, setFormData] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [localSpecsText, setLocalSpecsText] = useState("");
  const [closeCommentError, setCloseCommentError] = useState(false);

  /* ===== Suggestion State ===== */
  const [projectDetails, setProjectDetails] = useState([]);
  const [projectSuggestions, setProjectSuggestions] = useState([]);
  const [consultantSuggestions, setConsultantSuggestions] = useState([]);
  const [contractorSuggestions, setContractorSuggestions] = useState([]);
  const suggestionRef = useRef(null);

  /* ===== Helpers (MUST BE ABOVE useEffect) ===== */

  const clearSuggestions = () => {
    setProjectSuggestions([]);
    setConsultantSuggestions([]);
    setContractorSuggestions([]);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  /* ===== INIT ===== */

  useEffect(() => {
    if (row) {
      setFormData({ ...row });
      setLocalSpecsText(
        Array.isArray(row.Specifications)
          ? row.Specifications.join(", ")
          : row.Specifications || ""
      );
      setIsEditMode(false);
      setCloseCommentError(false);
      clearSuggestions();
    }
  }, [row]);

  /* ===== FETCH PROJECT DATA ===== */

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}project-names`)
      .then((res) => res.json())
      .then((data) => setProjectDetails(data.projectDetails || []))
      .catch(() => setProjectDetails([]));
  }, []);

  /* ===== CLOSE COMMENT VALIDATION ===== */

  useEffect(() => {
    if (formData?.CloseDate && !formData?.CloseComment?.trim()) {
      setCloseCommentError(true);
    } else {
      setCloseCommentError(false);
    }
  }, [formData?.CloseDate, formData?.CloseComment]);

  /* ===== CLICK OUTSIDE ===== */

  useEffect(() => {
    const handler = (e) => {
      if (suggestionRef.current && !suggestionRef.current.contains(e.target)) {
        clearSuggestions();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!open || !row) return null;

  /* ===== SUGGESTION LOGIC ===== */

  const handleSuggestionInput = (field, value) => {
    if (!value) return clearSuggestions();

    if (field === "ProjectName") {
      setProjectSuggestions(
        projectDetails.filter((p) =>
          p.projectName?.toLowerCase().startsWith(value.toLowerCase())
        )
      );
    }

    if (field === "Consultant") {
      setConsultantSuggestions(
        projectDetails.filter((p) =>
          p.consultant?.toLowerCase().startsWith(value.toLowerCase())
        )
      );
    }

    if (field === "Contractor") {
      setContractorSuggestions(
        projectDetails.filter((p) =>
          p.contractor?.toLowerCase().startsWith(value.toLowerCase())
        )
      );
    }
  };

  const applyProjectSuggestion = (p) => {
    setFormData((prev) => ({
      ...prev,
      ProjectName: p.projectName || "",
      Location: p.location || "",
      Consultant: p.consultant || "",
      Contractor: p.contractor || "",
    }));
    clearSuggestions();
  };

  /* ===== FIELD LIST ===== */

  const editableKeys =
    allowedColumns.length > 0
      ? Object.keys(row).filter(
          (k) => allowedColumns.includes(k) && !hiddenFields.includes(k)
        )
      : Object.keys(row).filter((k) => !hiddenFields.includes(k));

  /* ===== FIELD RENDER ===== */

  const renderField = (key) => {
    const label = fieldLabels[key] || key;
    const value = formData[key] ?? "";
    const isLocked = nonEditableFields.includes(key);
    const config = fieldTypes[key];

    /* ===== VIEW MODE ===== */
    if (!isEditMode || isLocked) {
      let displayValue = value;

      if (key.toLowerCase().includes("date") && value) {
        const d = new Date(value);
        if (!isNaN(d)) {
          displayValue = `${String(d.getDate()).padStart(2, "0")}-${String(
            d.getMonth() + 1
          ).padStart(2, "0")}-${d.getFullYear()}`;
        }
      }

      if (key === "Specifications" && Array.isArray(value)) {
        displayValue = value.join(", ");
      }

      return (
        <div className="udp-field">
          <label>{label}</label>
          <div className="udp-value">{displayValue || "—"}</div>
        </div>
      );
    }

    /* ===== SUGGESTION INPUTS ===== */

    if (["ProjectName", "Consultant", "Contractor"].includes(key)) {
      const suggestions =
        key === "ProjectName"
          ? projectSuggestions
          : key === "Consultant"
          ? consultantSuggestions
          : contractorSuggestions;

      return (
        <div
          className="udp-field"
          ref={suggestionRef}
          style={{ position: "relative" }}
        >
          <label>{label}</label>
          <TextField
            fullWidth
            value={value}
            onChange={(e) => {
              handleChange(key, e.target.value);
              handleSuggestionInput(key, e.target.value);
            }}
          />

          {suggestions.length > 0 && (
            <div className="suggestions-dropdown">
              {suggestions.map((s, i) => (
                <MenuItem
                  key={i}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (key === "ProjectName") applyProjectSuggestion(s);
                    else {
                      handleChange(
                        key,
                        key === "Consultant" ? s.consultant : s.contractor
                      );
                      clearSuggestions();
                    }
                  }}
                >
                  {key === "ProjectName"
                    ? s.projectName
                    : key === "Consultant"
                    ? s.consultant
                    : s.contractor}
                </MenuItem>
              ))}
            </div>
          )}
        </div>
      );
    }

    /* ===== DATE ===== */

    if (config === "date") {
      return (
        <div className="udp-field">
          <label>{label}</label>
          <TextField
            type="date"
            fullWidth
            value={value ? String(value).split("T")[0] : ""}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        </div>
      );
    }

    /* ===== TEXTAREA ===== */

    if (config === "textarea") {
      const restricted = key === "Comment" || key === "CloseComment";

      return (
        <div className="udp-field">
          <label>{label}</label>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={value}
            inputProps={{ maxLength: restricted ? 160 : undefined }}
            onChange={(e) => handleChange(key, e.target.value)}
            error={key === "CloseComment" && closeCommentError}
          />

          {restricted && (
            <div
              style={{
                fontSize: "12px",
                textAlign: "right",
                color: value.length >= 160 ? "#d32f2f" : "#666",
              }}
            >
              {value.length} / 160
            </div>
          )}

          {key === "CloseComment" && closeCommentError && (
            <div style={{ color: "#d32f2f", fontSize: "12px" }}>
              Close comment is required when Close Date is selected
            </div>
          )}
        </div>
      );
    }

    /* ===== SELECT ===== */

    if (config?.type === "select") {
      return (
        <div className="udp-field">
          <label>{label}</label>
          <Select
            fullWidth
            value={value || ""}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            <MenuItem value="" disabled>
              Select {label}
            </MenuItem>
            {config.options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </div>
      );
    }

    /* ===== DEFAULT ===== */

    return (
      <div className="udp-field">
        <label>{label}</label>
        <TextField
          fullWidth
          value={value}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      </div>
    );
  };

  /* ===== SAVE ===== */

  const handleSaveClick = () => {
    if (formData?.CloseDate && !formData?.CloseComment?.trim()) {
      toast.warning("Add close comment");
      return;
    }

    const payload = { ...formData };
    if ("Specifications" in payload) {
      payload.Specifications = localSpecsText
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }

    onSave(payload);
  };

  /* ===== RENDER ===== */

  return (
    <div className="udp-overlay">
      <div className="udp-box">
        <div className="udp-header">
          <h2>PROJECT / SERVICE DETAILS</h2>
        </div>

        <div className="udp-grid">
          {editableKeys.map((key) => (
            <div key={key}>{renderField(key)}</div>
          ))}
        </div>

        <div className="udp-footer">
          {!isEditMode ? (
            <>
              <Button variant="contained" onClick={() => setIsEditMode(true)}>
                Edit
              </Button>
              <Button variant="outlined" color="error" onClick={() => onDelete(row._id)}>
                Delete
              </Button>
              <Button variant="text" onClick={onClose}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" color="success" onClick={handleSaveClick}>
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFormData({ ...row });
                  setLocalSpecsText(
                    Array.isArray(row.Specifications)
                      ? row.Specifications.join(", ")
                      : row.Specifications || ""
                  );
                  setIsEditMode(false);
                  setCloseCommentError(false);
                  clearSuggestions();
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div> 
    </div>
  );
};

export default UniversalDetailsPopup;
